import './event-presenter.scss';
import { bindable, inject, observable } from 'aurelia';
import { SerieSettingEntity } from '../../apis/serie/serie-setting-entity';
import { CustomIntervalEntity } from '../../apis/custom-interval/custom-interval-entity';
import { ScheduleEntity } from '../../apis/schedule/schedule-entity';
import { EventEntity } from '../../apis/event/event-entity';
import { CustomIntervalApi } from '../../apis/custom-interval/custom-interval-api';
import { SerieApi } from '../../apis/serie/serie-api';
import { Project } from '../../model/project';
import { ScheduleRowEntity } from '../../apis/schedule/schedule-row-entity';
import { VenueEntity } from '../../apis/venue/venue-entity';
import { EventStatisticsEntity } from '../../apis/event-statistics/event-statistics-entity';
import { GroupEntity } from '../../apis/group/group-entity';
import { TeamSettingEntity } from '../../apis/team/team-setting-entity';
import { EventCommentEntity } from '../../apis/event/event-comment-entity';
import { ScheduleFieldEntity } from '../../apis/schedule/schedule-field-entity';
import { ScheduleDisplayNameService } from '../../services/schedule-display-name-service';

@inject(SerieApi, CustomIntervalApi, Project)
export class EventPresenter {

    @bindable public event: EventEntity = null;
    @bindable public schedules: IEventPresenterViewModel[] = null;
    @bindable public groups: GroupEntity[] = [];
    @bindable public onCancel: () => void;
    @bindable public statistics: EventStatisticsEntity = null;
    @bindable public comments: EventCommentEntity[] = [];
    @observable public selectedGroupId: string = '';
    public serie: SerieSettingEntity = null;
    public customIntervals: CustomIntervalEntity[] = [];
    public venues: VenueEntity[] = [];
    public fields: ScheduleFieldEntity[] = [];
    public teams: TeamSettingEntity[] = [];
    public schedule: IEventPresenterViewModel = null;
    public hideInfo: boolean = false;
    public isLoadingSchedule: boolean = false;
    public isFullscreen: boolean = false;
    public showAllComments: boolean = false;
    private _selectedScheduleGroupId: string = null;

    private static readonly _visibleCommentCount = 1;

    private _popStateHandler = (_e: PopStateEvent): void => {
        if (this.schedule != null) {
            this.schedule = null;
            this._selectedScheduleGroupId = null;
            this.isFullscreen = false;
        }
    };

    public toggleFullscreen(): void {
        this.isFullscreen = !this.isFullscreen;
    }

    public attached(): void {
        window.addEventListener('popstate', this._popStateHandler);
    }

    public detaching(): void {
        window.removeEventListener('popstate', this._popStateHandler);
    }

    public get name(): string {
        return this.schedule != null ? this._scheduleDisplayName(this.schedule) : '';
    }
    public get games(): ScheduleRowEntity[] {
        if (this.schedule == null) {
            return null;
        }
        const games = this.schedule.games ?? [];
        const activeGroupId = this._selectedScheduleGroupId ?? this.selectedGroupId;
        if (!activeGroupId) {
            return games;
        }
        const filteredGames = games.filter(g => g.groupId === activeGroupId);
        if (filteredGames.length > 0) {
            return filteredGames;
        }
        return games;
    }

    public toggleShowInfo(): void {
        this.hideInfo = !this.hideInfo;
    }
    public get toggleInfoTitleKey(): string {
        return `[title]${this.hideInfo ? 'ui:show_event_info' : 'ui:hide_event_info'}`;
    }
    public get showInfo(): boolean {
        return this.hideInfo === false && this.event !== null;
    }

    public get showEventInfo(): boolean {
        return this.hasEvent && !this.hideInfo && !this.hasSelectedSchedule;
    }

    public get filteredSchedules(): IEventPresenterViewModel[] {
        if (!this.schedules) { return []; }
        if (!this.selectedGroupId) { return this.schedules; }
        return this.schedules.filter(s => {
            const groupIds = s.schedule?.groupIds ?? [];
            return groupIds.includes(this.selectedGroupId);
        });
    }

    public get showScheduleList(): boolean {
        return this.filteredSchedules.length >= 1 && !this.hasSelectedSchedule;
    }

    public get presentableGroups(): GroupEntity[] {
        if (!this.groups) { return []; }
        const usedGroupIds = new Set<string>();
        for (const s of this.schedules ?? []) {
            for (const groupId of s.schedule?.groupIds ?? []) {
                usedGroupIds.add(groupId);
            }
        }
        return this.groups.filter(g => usedGroupIds.has(g.id));
    }

    public get showButtonGroup(): boolean {
        return this.hasEvent;
    }

    public onBackClick(): void {
        if (this.hasSelectedSchedule) {
            this.closeSchedule();
        } else {
            this.cancel();
        }
    }
    public get hasEvent(): boolean {
        return this.event != null;
    }

    public get code(): string {
        return (this.hasEvent && this.event.code != null) ? this.event.code : '';
    }

    public get hasMultipleSchedules(): boolean {
        return this.filteredSchedules != null && this.filteredSchedules.length > 1;
    }
    public get hasSelectedSchedule(): boolean {
        return this.schedule != null;
    }

    public get isMultiDayEvent(): boolean {
        if (!this.event || this.event.startDate == null || this.event.endDate == null) {
            return false;
        }
        return this.event.endDate.isAfter(this.event.startDate);
    }

    public scheduleDate(schedule: IEventPresenterViewModel): string {
        if (this.event == null || schedule == null || schedule.schedule == null || this.event.startDate == null) {
            return '';
        }
        return this.event.startDate.add(schedule.schedule.startDayOffset, 'days').toString();
    }

    public get selectedScheduleDate(): string {
        return this.scheduleDate(this.schedule);
    }

    public get baseUrl(): string {
        return window.location.host;
    }

    public get shareUrl(): string {
        return this.hasEvent ? `${window.location.origin}/${this.code}` : '';
    }

    public get shareTitle(): string {
        return this.hasEvent ? (this.event.title ?? this.event.organizer ?? window.location.host) : window.location.host;
    }

    public copyLinkSuccess: boolean = false;

    public async share(): Promise<void> {
        if (typeof navigator.share === 'function') {
            try {
                await navigator.share({ title: this.shareTitle, url: this.shareUrl });
            } catch {
                // User cancelled or share failed — ignore
            }
            return;
        }
        // Fallback: copy to clipboard
        try {
            await navigator.clipboard.writeText(this.shareUrl);
            this.copyLinkSuccess = true;
            setTimeout(() => { this.copyLinkSuccess = false; }, 2500);
        } catch {
            // ignore
        }
    }

    public get facebookShareUrl(): string {
        return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(this.shareUrl)}`;
    }

    public get whatsappShareUrl(): string {
        return `https://wa.me/?text=${encodeURIComponent(this.shareTitle + ' ' + this.shareUrl)}`;
    }

    public get supportsNativeShare(): boolean {
        return typeof navigator.share === 'function';
    }

    public closeSchedule(): void {
        this._selectedScheduleGroupId = null;
        // Route through history.back() so the pushed state entry is consumed,
        // keeping browser history consistent. The _popStateHandler clears this.schedule.
        history.back();
    }
    public cancel(): void {
        this.onCancel();
    }

    public async selectSchedule(schedule: IEventPresenterViewModel, groupId?: string): Promise<void> {
        this.isLoadingSchedule = true;
        try {
            this._selectedScheduleGroupId = groupId ?? null;
            const presentationData = await schedule.getPresentationData();
            this.serie = presentationData.serie;
            this.customIntervals = presentationData.customIntervals;
            this.venues = presentationData.venues;
            this.fields = presentationData.fields ?? [];
            this.teams = presentationData.teams ?? [];
            this.schedule = schedule;
            history.pushState({ scheduleSelected: true }, '');
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('selectSchedule failed:', err);
        } finally {
            this.isLoadingSchedule = false;
        }
    }

    public get schedulesGroupedByDay(): IDayGroup[] {
        if (this.filteredSchedules == null) {
            return [];
        }
        const groups = new Map<number, { vm: IEventPresenterViewModel; groupId?: string }[]>();
        for (const s of this.filteredSchedules) {
            const offset = s.schedule.startDayOffset ?? 0;
            if (!groups.has(offset)) {
                groups.set(offset, []);
            }
            const scheduleGroupIds = s.schedule.groupIds ?? [];
            const effectiveGroupIds = this.selectedGroupId
                ? scheduleGroupIds.filter(id => id === this.selectedGroupId)
                : scheduleGroupIds;
            const entries = effectiveGroupIds.length > 0 ? effectiveGroupIds : [null];
            for (const groupId of entries) {
                const resolvedGroupId = groupId ?? this.selectedGroupId;
                groups.get(offset).push({
                    vm: s,
                    groupId: resolvedGroupId ?? null,
                });
            }
        }
        return Array.from(groups.entries())
            .sort(([a], [b]) => a - b)
            .map(([offset, schedules]) => ({
                dayOffset: offset,
                dayLabel: `Dag ${offset + 1}`,
                date: (this.event != null && this.event.startDate != null)
                    ? this.event.startDate.add(offset, 'days').toString()
                    : '',
                schedules: schedules.map(s => ({
                    vm: s.vm,
                    groupId: s.groupId,
                    displayName: ScheduleDisplayNameService.getDisplayName(s.vm.schedule, this.groups, s.groupId || this.selectedGroupId),
                })),
            }));
    }

    public schedulesChanged(): void {
        // Always reset the selected schedule when a new schedules list arrives
        this.schedule = null;
        this._selectedScheduleGroupId = null;
    }

    private _scheduleDisplayName(vm: IEventPresenterViewModel): string {
        const activeGroupId = this._selectedScheduleGroupId ?? this.selectedGroupId;
        return ScheduleDisplayNameService.getDisplayName(vm.schedule, this.groups, activeGroupId);
    }

    public get visibleComments(): EventCommentEntity[] {
        if (!this.comments || this.comments.length === 0) { return []; }
        if (this.showAllComments) { return this.comments; }
        return this.comments.slice(0, EventPresenter._visibleCommentCount);
    }

    public get hasMoreComments(): boolean {
        return !this.showAllComments && this.comments != null && this.comments.length > EventPresenter._visibleCommentCount;
    }

    public get hasComments(): boolean {
        return this.comments != null && this.comments.length > 0;
    }

    public get remainingCommentCount(): number {
        return Math.max(0, (this.comments?.length ?? 0) - EventPresenter._visibleCommentCount);
    }

    public toggleShowAllComments(): void {
        this.showAllComments = !this.showAllComments;
    }
}

export interface IEventPresenterViewModel {
    readonly schedule: ScheduleEntity;
    readonly games: ScheduleRowEntity[];
    readonly name: string;
    readonly code: string;

    getPresentationData(): Promise<IEventPresenterScheduleData>;

}

export interface IEventPresenterScheduleData {
    readonly serie: SerieSettingEntity;
    readonly customIntervals: CustomIntervalEntity[];
    readonly venues: VenueEntity[];
    readonly teams: TeamSettingEntity[];
    readonly fields: ScheduleFieldEntity[];

}

export interface IDayGroup {
    dayOffset: number;
    dayLabel: string;
    date: string;
    schedules: { vm: IEventPresenterViewModel; groupId?: string; displayName: string }[];
}
